/**
 * nativeShare.ts
 *
 * Comparte archivos (imagen JPG) usando Capacitor Share + Filesystem.
 * Solo para APK. En web, usar navigator.share o fallback.
 */
import { isNativePlatform } from './platform';

/**
 * Comparte una imagen (blob) usando la API nativa de Capacitor.
 * En APK: guarda archivo temporal → abre share sheet nativo (WhatsApp, Telegram, etc.)
 * En web: usa navigator.share como fallback.
 *
 * @returns true si se compartió, false si fue cancelado o falló
 */
export async function shareImageNative(
  blob: Blob,
  fileName: string,
  title?: string,
  text?: string,
): Promise<boolean> {
  if (isNativePlatform()) {
    try {
      // Importar dinámicamente para no romper web
      const { Filesystem, Directory } = await import('@capacitor/filesystem');
      const { Share } = await import('@capacitor/share');

      // Convertir blob a base64
      const base64 = await blobToBase64(blob);

      // Guardar en directorio cache (temporal)
      const result = await Filesystem.writeFile({
        path: fileName,
        data: base64,
        directory: Directory.Cache,
      });

      // Compartir vía share sheet nativo
      await Share.share({
        title: title || fileName,
        text: text || undefined,
        files: [result.uri],
        dialogTitle: title || 'Compartir ticket',
      });

      return true;
    } catch (err: any) {
      if (err?.message?.includes('canceled') || err?.message?.includes('cancelled')) {
        return false; // Usuario canceló
      }
      console.error('[nativeShare] Error compartiendo:', err);
      throw err;
    }
  }

  // Fallback web: navigator.share
  const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
  if (navigator.share && navigator.canShare) {
    const shareData: ShareData = { files: [file], title: title || fileName };
    if (text) shareData.text = text;
    if (navigator.canShare(shareData)) {
      await navigator.share(shareData);
      return true;
    }
  }

  // Último fallback: descargar
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
  return true;
}

/** Convierte un Blob a string base64 (sin prefijo data:) */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Quitar prefijo "data:image/jpeg;base64,"
      const base64 = result.split(',')[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
